import type { UIMessage } from "ai"
import { agentChatName } from "./chat-cache"

/**
 * Decision 0137: an agent's chat keeps a bounded live window; older messages are archived in pages:
 *
 * - `GET …/chat-archive`: the page list (`{messages, pages: [{page, firstPos, count, firstAt, lastAt}]}`);
 * - `GET …/chat-archive?page=N`: one page as stored (`{messages: [{pos, at, message}]}`).
 *
 * Both have the access of `/get-messages` (members of the workspace with access to the agent's project). Decision
 * 0160: the chat loads them after the live window's older pages, on scroll-up, through `chat-history.ts`.
 */

export interface ArchivePageInfo {
  readonly page: number
  readonly firstPos: number
  readonly count: number
  readonly firstAt: number | null
  readonly lastAt: number | null
}

export interface ArchiveIndex {
  readonly messages: number
  readonly pages: ReadonlyArray<ArchivePageInfo>
}

/** The body path of an agent's archive (the page list, or one page). */
export const archiveUrl = (workspaceId: string, agentId: string, page?: number): string =>
  `/api/agents/agent-body/${encodeURIComponent(agentChatName(workspaceId, agentId))}/chat-archive${
    page === undefined ? "" : `?page=${page}`
  }`

/** The archived messages followed by the live window; a message in both (a tab that loaded it before the cut) once. */
export const withArchived = (
  older: ReadonlyArray<UIMessage>,
  live: ReadonlyArray<UIMessage>,
): ReadonlyArray<UIMessage> => {
  if (older.length === 0) return live
  const liveIds = new Set(live.map((m) => m.id))
  return [...older.filter((m) => !liveIds.has(m.id)), ...live]
}

const asUrl = (path: string): string => new URL(path, globalThis.location?.href ?? "http://localhost").href

/** The archive's page list. */
export const fetchArchiveIndex = async (
  workspaceId: string,
  agentId: string,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<ArchiveIndex> => {
  const r = await fetchFn(asUrl(archiveUrl(workspaceId, agentId)), { credentials: "same-origin" })
  if (!r.ok) throw new Error(`chat-archive ${r.status}`)
  const index = (await r.json()) as ArchiveIndex
  if (!Array.isArray(index?.pages)) throw new Error("chat-archive: not a page list")
  return index
}

/** One archived page's messages, oldest first. */
export const fetchArchivePage = async (
  workspaceId: string,
  agentId: string,
  page: number,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<ReadonlyArray<UIMessage>> => {
  const r = await fetchFn(asUrl(archiveUrl(workspaceId, agentId, page)), { credentials: "same-origin" })
  if (!r.ok) throw new Error(`chat-archive ${r.status}`)
  const raw = (await r.json()) as { messages?: ReadonlyArray<{ message?: UIMessage }> }
  return (raw.messages ?? []).flatMap((e) => (e.message ? [e.message] : []))
}
